import { useMemo, useEffect } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import FacilityWaitingListsApi from 'libs/api/facility/waitingLists';
import { useNavigate } from 'hooks/useNavigate';
import { useRecoilValue } from 'recoil';
import { getShops, getInactiveShops } from 'hooks/useShops';
import { useApi } from 'hooks/useApi';
import WaitingListAdapter from './waitingList.adapter';
import moment from 'moment';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import LinkWithParams from '../../../../components/linkWithParams';
import { useFilter } from '../../../../hooks/useFilter';

const api = new FacilityWaitingListsApi();

const useWaitingLists = (isMainPage = true, isEdit) => {
  const { t } = useTranslation();
  let { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const { filterData } = useFilter();
  const [form] = Form.useForm();
  const {
    data,
    onChangeTableFilter,
    loading,
    updateInProcess,
    handleDownloadExcel,
    singleItem,
    setUpdateInProcess,
    onDelete,
  } = useApi({
    resourceKey: 'waiting_lists',
    singleResourceKey: 'waiting_list',
    api: api,
    isMainPage,
    adapter: WaitingListAdapter,
  });

  const extrastockTypes = useMemo(
    () => ({
      less_then_20: t('Fino a 20 mq'),
      more_then_20: t('Oltre 20 mq'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Data dal'),
        dataIndex: 'date_from',
        sorter: () => {},
        render: (data) => moment(data).format('DD MMMM YYYY'),
      },
      {
        title: t('Data al'),
        dataIndex: 'date_to',
        sorter: () => {},
        render: (data) => moment(data).format('DD MMMM YYYY'),
      },

      {
        title: t('Tipologia Stock'),
        dataIndex: 'extrastock_type',
        sorter: () => {},
        render: (data) => extrastockTypes[data],
      },

      {
        title: t('Nome richiedente'),
        dataIndex: 'manager_name',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <Link to={`${pathname}/${row.id}`}>
                <Button icon={<EyeOutlined />} />
              </Link>
            </Col>
            {row.can_update !== false && (
              <Col>
                <Link to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </Link>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </LinkWithParams>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [pathname, extrastockTypes]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
    ],
    [t, filterData],
  );

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    const data = {
      waiting_list: {
        ...values,
      },
    };
    try {
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          company: 'company_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'date_to':
                case 'date_from': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'YYYY-MM-DD'),
                  });
                  break;
                }
                case 'company': {
                  fieldsValues.push({
                    name: 'company_id',
                    value: singleItem[key].id,
                  });
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  return {
    columns,
    data,
    loading,
    onChangeTableFilter,
    filters,
    shops,
    inactiveShops,
    extrastockTypes,
    form,
    updateInProcess,
    singleItem,
    handleDownloadExcel,
    handleCreateOrUpdate,
    onDelete,
  };
};

export default useWaitingLists;
